<template>
  <div class="page-container">
    <el-card style="margin-bottom: 20px;">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-input 
            v-model="searchKeyword" 
            placeholder="搜索标题或描述" 
            clearable
            @clear="handleSearch"
            @keyup.enter.native="handleSearch">
            <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
          </el-input>
        </el-col>
        <el-col :span="4">
          <el-select v-model="categoryFilter" placeholder="筛选分类" clearable @change="handleSearch" style="width: 100%;">
            <el-option label="全部分类" :value="null"></el-option>
            <el-option v-for="cat in categories" :key="cat.id" :label="cat.name" :value="cat.id"></el-option>
          </el-select>
        </el-col>
        <el-col :span="4">
          <el-select v-model="statusFilter" placeholder="筛选状态" clearable @change="handleSearch" style="width: 100%;">
            <el-option label="全部状态" :value="null"></el-option>
            <el-option label="草稿" :value="0"></el-option>
            <el-option label="已发布" :value="1"></el-option>
            <el-option label="已下架" :value="2"></el-option>
          </el-select>
        </el-col>
        <el-col :span="10">
          <el-button 
            v-if="hasPermission('resource:add')"
            type="primary" 
            @click="handleAdd" 
            icon="el-icon-plus">
            {{ getButtonName('resource:add', '新增资源') }}
          </el-button>
          <el-button 
            v-if="hasPermission('resource:ai-generate')"
            type="success" 
            @click="handleBatchAiGenerate" 
            icon="el-icon-magic-stick">
            {{ getButtonName('resource:ai-generate', 'AI批量生成') }}
          </el-button>
          <el-button @click="handleReset" icon="el-icon-refresh">重置</el-button>
        </el-col>
      </el-row>
    </el-card>

    <el-card>
      <!-- 批量操作按钮栏 -->
      <div style="margin-bottom: 15px; padding: 10px; background: #f0f9ff; border-radius: 4px;">
        <span style="margin-right: 15px; color: #409EFF;">
          <i class="el-icon-info"></i> 已选择 {{ selectedRows.length }} 项
        </span>
        <el-button 
          v-if="hasPermission('resource:batch-top')"
          size="small" 
          type="warning" 
          @click="handleBatchTop(true)" 
          :disabled="selectedRows.length === 0">
          {{ getButtonName('resource:batch-top', '批量置顶') }}
        </el-button>
        <el-button 
          v-if="hasPermission('resource:batch-untop')"
          size="small" 
          type="info"
          @click="handleBatchTop(false)" 
          :disabled="selectedRows.length === 0">
          {{ getButtonName('resource:batch-untop', '批量取消置顶') }}
        </el-button>
        <el-button 
          v-if="hasPermission('resource:seo-push')"
          size="small" 
          type="success" 
          icon="el-icon-s-promotion" 
          @click="handleBatchPushBaidu" 
          :disabled="selectedRows.length === 0">
          {{ getButtonName('resource:seo-push', '批量推送百度') }}
        </el-button>
        <el-button size="small" @click="clearSelection">清空选择</el-button>
      </div>
      
      <el-table 
        :data="tableData" 
        border 
        v-loading="loading"
        @selection-change="handleSelectionChange"
        ref="resourceTable"
        style="width: 100%">
        <el-table-column type="selection" width="55"></el-table-column>
        <el-table-column prop="name" label="名称" width="160"></el-table-column>
        <el-table-column prop="title" label="标题" width="200"></el-table-column>
        <el-table-column prop="tags" label="标签" width="200">
          <template slot-scope="scope">
            <el-tag
              v-for="tag in (scope.row.tags ? scope.row.tags.split(',') : [])"
              :key="tag"
              size="mini"
              style="margin-right: 4px; margin-bottom: 2px;"
            >
              {{ tag }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="分类" width="120">
          <template slot-scope="scope">
            {{ getCategoryName(scope.row.categoryId) }}
          </template>
        </el-table-column>
        <el-table-column label="类型" width="100">
          <template slot-scope="scope">
            {{ getResourceType(scope.row.resourceType) }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.status === 0" type="info">草稿</el-tag>
            <el-tag v-else-if="scope.row.status === 1" type="success">已发布</el-tag>
            <el-tag v-else-if="scope.row.status === 2" type="warning">已下架</el-tag>
            <el-tag v-else type="danger">未知状态</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="描述" min-width="300">
          <template slot-scope="scope">
            <div class="desc-cell">{{ scope.row.description }}</div>
          </template>
        </el-table-column>
        <el-table-column label="操作" fixed="right" width="150" align="center" class-name="operation-column">
          <template slot-scope="scope">
            <el-button size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
        :current-page="page.current"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="page.size"
        layout="total, sizes, prev, pager, next, jumper"
        :total="page.total"
        style="margin-top: 20px; text-align: right;">
      </el-pagination>
    </el-card>

    <!-- 编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="60%" :close-on-click-modal="false">
      <el-form :model="form" label-width="100px">
        <el-form-item label="分类">
          <el-select v-model="form.categoryId">
            <el-option v-for="cat in categories" :key="cat.id" :label="cat.name" :value="cat.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="资源类型">
          <el-select v-model="form.resourceType">
            <el-option label="软件" :value="0"></el-option>
            <el-option label="网站" :value="1"></el-option>
            <el-option label="视频教程" :value="2"></el-option>
            <el-option label="图文教程" :value="3"></el-option>
            <el-option label="其他" :value="4"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="名称">
          <div style="display: flex; gap: 10px; align-items: center;">
            <el-input v-model="form.name" placeholder="仅后台维护使用" style="flex: 1;"></el-input>
            <el-button 
              type="primary" 
              icon="el-icon-magic-stick" 
              @click="handleAiGenerate"
              :loading="aiGenerating"
              :disabled="!form.name || !form.name.trim()"
            >
              AI生成
            </el-button>
          </div>
        </el-form-item>
        <el-form-item label="标题">
          <el-input v-model="form.title"></el-input>
        </el-form-item>
        <el-form-item label="标签">
          <el-select
            v-model="tagsList"
            multiple
            filterable
            allow-create
            default-first-option
            placeholder="输入后回车创建标签"
            style="width: 100%;"
          >
            <el-option v-for="tag in availableTags" :key="tag" :label="tag" :value="tag" />
          </el-select>
        </el-form-item>
        <el-form-item label="封面图片">
          <div class="cover-upload-container">
            <!-- 图片预览区域 -->
            <div v-if="form.coverImage" class="cover-preview-wrapper">
              <img :src="form.coverImage" class="cover-image">
              <div class="cover-actions">
                <el-button size="mini" type="primary" icon="el-icon-edit" @click="showCropDialog">裁剪</el-button>
                <el-button size="mini" type="danger" icon="el-icon-delete" @click="removeCover">删除</el-button>
                <el-button size="mini" type="success" icon="el-icon-upload" @click="triggerUpload">更换</el-button>
              </div>
            </div>
            
            <!-- 上传区域 -->
            <el-upload
              v-show="!form.coverImage"
              class="cover-uploader"
              action="/api/upload/cover"
              :headers="uploadHeaders"
              :show-file-list="false"
              :on-success="handleCoverSuccess"
              :before-upload="beforeCoverUpload"
              drag
              ref="coverUpload">
              <div class="cover-uploader-placeholder">
                <i class="el-icon-upload cover-uploader-icon"></i>
                <div class="cover-uploader-text">将图片拖到此处，或<em>点击上传</em></div>
                <div class="cover-uploader-tip">支持 Ctrl+V 粘贴图片</div>
              </div>
            </el-upload>
          </div>
          <div class="tip">建议尺寸：800x600，支持jpg/png格式，大小不超过2MB</div>
        </el-form-item>
        <el-form-item label="描述">
          <el-input type="textarea" v-model="form.description" :rows="3"></el-input>
        </el-form-item>
        <el-form-item label="是否免费">
          <el-switch v-model="form.isFree" :active-value="1" :inactive-value="0"></el-switch>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="form.status">
            <el-option label="草稿" :value="0"></el-option>
            <el-option label="已发布" :value="1"></el-option>
            <el-option label="已下架" :value="2"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="内容">
          <quill-editor
            v-model="form.content"
            :options="editorOption"
            style="height: 400px;"
          >
          </quill-editor>
        </el-form-item>
        
        <el-divider content-position="left">分享链接</el-divider>
        
        <el-form-item label="网盘类型">
          <el-select v-model="form.shareType" placeholder="请选择网盘类型" clearable @change="handleShareTypeChange">
            <el-option 
              v-for="item in diskTypes" 
              :key="item.itemValue" 
              :label="item.itemLabel" 
              :value="item.itemValue">
            </el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item label="分享链接" v-if="form.shareType">
          <el-input v-model="form.shareLink" placeholder="请输入分享链接">
            <i slot="prefix" class="el-icon-link"></i>
          </el-input>
        </el-form-item>
        
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave">保存</el-button>
      </span>
    </el-dialog>

    <!-- 裁剪对话框 -->
    <el-dialog title="裁剪封面图片" :visible.sync="cropDialogVisible" width="900px" :close-on-click-modal="false" @close="handleCropDialogClose" @opened="handleCropDialogOpened">
      <div class="crop-container" v-if="cropDialogVisible">
        <div class="crop-main">
          <div class="crop-area">
            <vue-cropper
              v-if="cropImageSrc && cropperReady"
              ref="cropper"
              :img="cropImageSrc"
              :outputSize="1"
              :outputType="'jpeg'"
              :info="true"
              :full="false"
              :canMove="true"
              :canMoveBox="true"
              :original="false"
              :autoCrop="true"
              :autoCropWidth="800"
              :autoCropHeight="600"
              :fixed="true"
              :fixedNumber="[4, 3]"
              :centerBox="true"
              :fixedBox="false"
              @realTime="updatePreviews"
            >
            </vue-cropper>
            <div v-else class="crop-loading">
              <i class="el-icon-loading"></i>
              <p>加载中...</p>
            </div>
          </div>
        </div>
        <div class="crop-preview">
          <div class="preview-title">预览</div>
          <div class="preview-box" :style="{ width: '200px', height: '150px', overflow: 'hidden' }">
            <div v-if="previews.url" :style="previews.div" class="preview-container">
              <img :src="previews.url" :style="previews.img">
            </div>
            <div v-else class="preview-placeholder">
              <i class="el-icon-picture-outline"></i>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="cropDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmCrop" :loading="cropping" :disabled="!cropperReady">确定裁剪</el-button>
      </div>
    </el-dialog>

    <!-- AI批量生成弹窗 -->
    <el-dialog 
      title="AI批量生成资源" 
      :visible.sync="batchAiDialogVisible" 
      width="700px"
      :close-on-click-modal="false">
      <el-form label-width="120px">
        <el-form-item label="资源类型">
          <el-select v-model="batchAiForm.resourceType" placeholder="请选择资源类型" style="width: 100%;">
            <el-option label="软件" :value="0"></el-option>
            <el-option label="网站" :value="1"></el-option>
            <el-option label="视频教程" :value="2"></el-option>
            <el-option label="图文教程" :value="3"></el-option>
            <el-option label="其他" :value="4"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="默认分类">
          <el-select v-model="batchAiForm.categoryId" placeholder="请选择分类" style="width: 100%;">
            <el-option v-for="cat in categories" :key="cat.id" :label="cat.name" :value="cat.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="资源名称列表">
          <el-input
            type="textarea"
            v-model="batchAiForm.nameList"
            :rows="12"
            placeholder="请输入资源名称，每行一个，例如：&#10;Notepad++ 文本编辑器&#10;uTools 轻工具&#10;Visual Studio Code 代码编辑器">
          </el-input>
          <div style="margin-top: 10px; color: #909399; font-size: 12px;">
            <i class="el-icon-info"></i> 提示：每行一个资源名称，AI将自动生成标题、描述、内容和标签，生成的资源为<strong>草稿</strong>状态
          </div>
        </el-form-item>
      </el-form>
      
      <div v-if="batchAiProcessing || batchAiLogs.length > 0" style="margin-top: 20px;">
        <el-progress 
          :percentage="batchAiProgress" 
          :status="batchAiStatus"
          :format="formatBatchProgress"></el-progress>
        <div class="batch-ai-logs" style="margin-top: 15px; max-height: 400px; overflow-y: auto; background: #f5f7fa; padding: 15px; border-radius: 4px; font-family: 'Consolas', 'Monaco', monospace;">
          <div v-for="(log, index) in batchAiLogs" :key="index" style="margin-bottom: 5px; font-size: 13px; line-height: 1.8;" :style="{ color: log.type === 'error' ? '#f56c6c' : log.type === 'success' ? '#67c23a' : '#606266', fontWeight: log.type === 'error' || log.type === 'success' ? '500' : 'normal' }">
            {{ log.message }}
          </div>
        </div>
      </div>
      
      <div slot="footer">
        <el-button @click="batchAiDialogVisible = false" :disabled="batchAiProcessing">取消</el-button>
        <el-button 
          type="primary" 
          @click="startBatchAiGenerate" 
          :loading="batchAiProcessing"
          :disabled="!batchAiForm.nameList || !batchAiForm.categoryId">
          {{ batchAiProcessing ? '生成中...' : '开始生成' }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 批量推送百度对话框 -->
    <el-dialog
      title="批量推送到百度"
      :visible.sync="baiduPushDialogVisible"
      width="600px"
      :close-on-click-modal="false">
      <el-form label-width="120px">
        <el-form-item label="选中资源">
          <el-tag type="info">{{ selectedRows.length }} 个资源</el-tag>
        </el-form-item>
        <el-form-item label="百度站长Token">
          <el-input
            v-model="baiduToken"
            placeholder="选填，不填写将使用服务器配置的默认Token"
            clearable>
            <template slot="append">
              <el-button @click="openBaiduHelp" icon="el-icon-question">Token获取帮助</el-button>
            </template>
          </el-input>
          <div style="font-size: 12px; color: #909399; margin-top: 8px;">
            <i class="el-icon-info"></i> 如不填写，系统将使用服务器配置文件中的默认Token<br>
            <i class="el-icon-warning" style="color: #E6A23C;"></i> 每次最多推送2000条链接，新站配额每天500条，普通站每天10万条<br>
            <i class="el-icon-info" style="color: #409EFF;"></i> 如提示超配额，请明天再试或检查是否今日已推送过
          </div>
        </el-form-item>
        
        <!-- 推送结果 -->
        <el-form-item label="推送结果" v-if="pushResult">
          <el-alert
            :type="pushResult.success ? 'success' : 'warning'"
            :title="pushResult.success ? '推送成功' : '推送失败'"
            :closable="false">
            <div v-if="pushResult.success">
              <p><i class="el-icon-success"></i> 成功推送 {{ pushResult.count }} 个链接到百度</p>
              <div v-if="pushResult.baiduResponse" style="font-size: 12px; margin-top: 12px; background: #f5f7fa; padding: 10px; border-radius: 4px;">
                <p style="color: #303133; margin-bottom: 6px;"><strong>百度API响应：</strong></p>
                <pre style="margin: 0; color: #67C23A; white-space: pre-wrap; word-break: break-all;">{{ pushResult.baiduResponse }}</pre>
                
                <!-- 解析百度返回的配额信息 -->
                <div v-if="getBaiduQuota(pushResult.baiduResponse)" style="margin-top: 10px; padding-top: 10px; border-top: 1px dashed #dcdfe6;">
                  <p style="color: #409EFF; margin: 0;">
                    <i class="el-icon-info"></i> {{ getBaiduQuota(pushResult.baiduResponse) }}
                  </p>
                </div>
                
                <!-- 状态码显示 -->
                <p v-if="pushResult.statusCode" style="color: #909399; margin-top: 8px; margin-bottom: 0;">
                  HTTP状态码: {{ pushResult.statusCode }}
                </p>
              </div>
              
              <!-- 推送说明 -->
              <div style="margin-top: 12px; padding: 8px; background: #e6f7ff; border-left: 3px solid #1890ff; font-size: 12px; color: #0050b3;">
                <p style="margin: 0;"><i class="el-icon-warning"></i> <strong>注意事项：</strong></p>
                <p style="margin: 4px 0 0 0;">1. 推送成功后，百度需要 <strong>几小时到24小时</strong> 处理数据</p>
                <p style="margin: 4px 0 0 0;">2. 数据不会立即显示在百度站长平台，请耐心等待</p>
                <p style="margin: 4px 0 0 0;">3. 如果24小时后仍未显示，请检查网站是否已在百度站长平台验证</p>
              </div>
            </div>
            <div v-else>
              <p>{{ pushResult.message || '推送失败' }}</p>
              <p v-if="pushResult.error" style="font-size: 12px; color: #E6A23C; margin-top: 8px;">
                错误信息: {{ pushResult.error }}
              </p>
            </div>
          </el-alert>
          
          <!-- 推送的URL列表 -->
          <div v-if="pushResult.urls && pushResult.urls.length > 0" style="margin-top: 12px;">
            <div style="font-size: 13px; color: #606266; margin-bottom: 8px;">推送的链接：</div>
            <el-input
              type="textarea"
              :value="pushResult.urls.join('\n')"
              :rows="6"
              readonly
              style="font-size: 12px;">
            </el-input>
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="baiduPushDialogVisible = false">关闭</el-button>
        <el-button
          type="primary"
          @click="confirmPushBaidu"
          :loading="pushingBaidu">
          {{ pushingBaidu ? '推送中...' : '开始推送' }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { quillEditor, Quill } from 'vue-quill-editor'
import VueCropper from 'vue-cropper'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'

// 注意：quill-image-resize-module 与 Quill 1.3.7 不兼容
// 改用 CSS 方式实现图片调整大小

export default {
  name: 'ResourceManage',
  components: { 
    quillEditor, 
    VueCropper: VueCropper.VueCropper || VueCropper 
  },
  data() {
    return {
      tableData: [],
      categories: [],
      loading: false,
      searchKeyword: '',
      categoryFilter: null,
      statusFilter: null,
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      dialogVisible: false,
      dialogTitle: '',
      form: {},
      tagsList: [],
      availableTags: [],
      diskTypes: [], // 网盘类型字典
      aiGenerating: false,
      selectedRows: [], // 选中的行
      userPermissions: [], // 用户权限列表
      originalCoverImage: '', // 原始封面URL，用于编辑时保护
      coverDeleted: false, // 用户是否主动删除了封面
      // 裁剪相关
      cropDialogVisible: false,
      cropImageSrc: '',
      cropperReady: false,
      cropping: false,
      previews: {},
      // 批量AI生成相关
      batchAiDialogVisible: false,
      batchAiProcessing: false,
      batchAiProgress: 0,
      batchAiStatus: null,
      batchAiLogs: [],
      batchAiForm: {
        nameList: '',
        resourceType: 0,
        categoryId: null
      },
      permissionMap: {}, // 权限key到name的映射
      permissionSortMap: {}, // 权限key到sort的映射
      quillEditor: null, // 富文本编辑器实例
      // 百度推送相关
      baiduPushDialogVisible: false,
      baiduToken: '', // 百度站长token
      pushingBaidu: false,
      pushResult: null
    }
  },
  computed: {
    uploadHeaders() {
      return {
        Authorization: `Bearer ${localStorage.getItem('admin_token')}`
      }
    },
    // 富文本编辑器配置
    editorOption() {
      const modules = {
        toolbar: {
          container: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            ['link', 'image'],
            ['clean']
          ],
          handlers: {
            image: () => {
              this.handleImageUpload()
            }
          }
        }
      }
      
      return {
        modules,
        placeholder: '请输入资源内容...'
      }
    }
  },
  mounted() {
    this.loadCategories()
    this.loadData()
    this.loadUserPermissions()
    this.loadAvailableTags()
    this.loadDiskTypes() // 加载网盘类型字典
    this.loadPermissionNames() // 加载权限名称
    // 添加粘贴监听（封面图片）
    document.addEventListener('paste', this.handlePaste)
    // 初始化富文本编辑器后添加粘贴监听
    this.$nextTick(() => {
      this.initEditorPasteHandler()
    })
  },
  beforeDestroy() {
    // 移除粘贴监听
    document.removeEventListener('paste', this.handlePaste)
    // 移除富文本编辑器粘贴监听
    if (this.quillEditor) {
      const editor = this.quillEditor.quill
      if (editor && editor.root) {
        editor.root.removeEventListener('paste', this.handleEditorPaste)
      }
    }
  },
  watch: {
    // 监听裁剪器变化，实时更新预览
    cropDialogVisible(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.$refs.cropper) {
            this.$refs.cropper.startCrop()
          }
        })
      }
    },
    // 监听对话框打开，初始化编辑器
    dialogVisible(val) {
      if (val) {
        this.$nextTick(() => {
          this.initEditorPasteHandler()
        })
      }
    }
  },
  methods: {
    async loadCategories() {
      const res = await this.$axios.get('/admin/category/list')
      if (res.data.code === 200) {
        this.categories = res.data.data
      }
    },
    // 加载网盘类型字典
    async loadDiskTypes() {
      try {
        const res = await this.$axios.get('/admin/dict/type/disk_type')
        if (res.data.code === 200) {
          this.diskTypes = res.data.data || []
        }
      } catch (error) {
        console.error('加载网盘类型失败:', error)
        // 如果加载失败，使用默认值
        this.diskTypes = [
          { itemLabel: '百度网盘', itemValue: 'baidu' },
          { itemLabel: '夸克网盘', itemValue: 'quark' },
          { itemLabel: '阿里云盘', itemValue: 'aliyun' },
          { itemLabel: '123网盘', itemValue: 'pan123' },
          { itemLabel: '迅雷网盘', itemValue: 'xunlei' },
          { itemLabel: '官网下载', itemValue: 'official' },
          { itemLabel: '其他', itemValue: 'other' }
        ]
      }
    },
    async loadData() {
      this.loading = true
      try {
        const res = await this.$axios.get('/admin/resource/page', {
          params: {
            current: this.page.current,
            size: this.page.size,
            keyword: this.searchKeyword,
            categoryId: this.categoryFilter,
            status: this.statusFilter
          }
        })
        if (res.data.code === 200) {
          this.tableData = res.data.data.records
          this.page.total = res.data.data.total
        }
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.loadData()
    },
    handleReset() {
      this.searchKeyword = ''
      this.categoryFilter = null
      this.statusFilter = null
      this.page.current = 1
      this.loadData()
    },
    handlePageChange(page) {
      this.page.current = page
      this.loadData()
    },
    handleSizeChange(size) {
      this.page.size = size
      this.page.current = 1
      this.loadData()
    },
    handleAdd() {
      this.dialogTitle = '新增资源'
      this.form = {
        title: '',
        name: '',
        description: '',
        content: '',
        categoryId: null,
        resourceType: 0,
        isFree: 1,
        status: 1,
        coverImage: '',
        shareType: 'baidu',  // 默认选择百度网盘
        shareLink: '',   // 分享链接
        tags: ''
      }
      this.tagsList = []
      this.coverDeleted = false // 重置删除标识
      this.dialogVisible = true
    },
    async handleEdit(row) {
      this.dialogTitle = '编辑资源'
      
      try {
        // 从后端加载完整的资源详情（包括 content 字段）
        const res = await this.$axios.get(`/admin/resource/${row.id}`)
        if (res.data.code === 200) {
          this.form = { ...res.data.data }
          this.tagsList = (res.data.data.tags ? res.data.data.tags.split(',') : [])
          // 保存原始封面URL，防止丢失
          this.originalCoverImage = res.data.data.coverImage || ''
          this.coverDeleted = false // 重置删除标识
          this.dialogVisible = true
        } else {
          this.$message.error('加载资源详情失败')
        }
      } catch (error) {
        console.error('加载资源详情失败:', error)
        this.$message.error('加载资源详情失败')
      }
    },
    async handleSave() {
      try {
        this.form.tags = (this.tagsList || []).filter(t => t && t.trim()).join(',')
        
        // 准备要提交的数据
        const submitData = { ...this.form }
        
        // 封面保护机制
        if (this.form.id && this.originalCoverImage) {
          // 编辑模式：如果用户没有主动删除封面，则保持原始封面
          if (!this.coverDeleted) {
            // 如果当前封面为空或者是无效值，则恢复原始封面
            if (!submitData.coverImage || !submitData.coverImage.startsWith('http')) {
              submitData.coverImage = this.originalCoverImage
            }
          }
        }
        
        if (this.form.id) {
          await this.$axios.put(`/admin/resource/${this.form.id}`, submitData)
        } else {
          await this.$axios.post('/admin/resource', submitData)
        }
        this.$message.success('保存成功')
        this.dialogVisible = false
        this.originalCoverImage = '' // 清空原始封面
        this.coverDeleted = false // 清空删除标识
        this.loadData()
      } catch (error) {
        console.error('保存失败:', error)
        this.$message.error('保存失败')
      }
    },
    handleDelete(row) {
      this.$confirm('确定删除该资源?', '提示', {
        type: 'warning'
      }).then(async () => {
        await this.$axios.delete(`/admin/resource/${row.id}`)
        this.$message.success('删除成功')
        this.loadData()
      })
    },
    async handleReview(row, status) {
      const action = status === 1 ? '通过' : '拒绝'
      this.$confirm(`确定${action}该资源?`, '审核确认', {
        type: 'warning'
      }).then(async () => {
        try {
          await this.$axios.post(`/admin/resource/review/${row.id}`, { status })
          this.$message.success(`审核${action}成功${status === 1 ? '，用户已获得额外下载次数' : ''}`)
          this.loadData()
        } catch (error) {
          this.$message.error('审核失败')
        }
      })
    },
    getResourceType(type) {
      const map = { 0: '软件', 1: '网站', 2: '视频教程', 3: '图文教程', 4: '其他' }
      return map[type] || '未知'
    },
    getCategoryName(categoryId) {
      if (!categoryId) return '未分类'
      const category = this.categories.find(cat => cat.id === categoryId)
      return category ? category.name : '未知分类'
    },
    // 加载权限名称
    async loadPermissionNames() {
      try {
        const menuData = localStorage.getItem('admin_menu_data')
        if (menuData) {
          const menus = JSON.parse(menuData)
          const allPermissions = menus.permissions || []
          
          // 构建权限key到name和sort的映射
          this.permissionMap = {}
          this.permissionSortMap = {}
          allPermissions.forEach(p => {
            this.permissionMap[p.key] = p.name
            this.permissionSortMap[p.key] = p.sort || 999 // 默认排到最后
          })
        }
      } catch (error) {
        console.error('加载权限名称失败:', error)
      }
    },
    // 获取按钮名称（优先使用后端返回的名称）
    getButtonName(permissionKey, defaultName) {
      return this.permissionMap[permissionKey] || defaultName
    },
    beforeCoverUpload(file) {
      const isImage = file.type.startsWith('image/')
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isImage) {
        this.$message.error('只能上传图片格式!')
        return false
      }
      if (!isLt2M) {
        this.$message.error('图片大小不能超过 2MB!')
        return false
      }
      return true
    },
    handleCoverSuccess(response) {
      console.log('封面上传响应:', response)
      if (response.code === 200) {
        this.form.coverImage = response.data.url
        this.$forceUpdate() // 强制更新视图
        this.$message.success('封面上传成功')
        console.log('封面URL:', this.form.coverImage)
      } else {
        this.$message.error('封面上传失败: ' + (response.msg || response.message || '未知错误'))
      }
    },
    handleShareTypeChange() {
      // 切换网盘类型时清空链接
      this.form.shareLink = ''
    },
    async handleAiGenerate() {
      if (!this.form.name || !this.form.name.trim()) {
        this.$message.warning('请先输入资源名称')
        return
      }
      
      this.aiGenerating = true
      try {
        const res = await this.$axios.post('/admin/ai/generate-resource-info', {
          name: this.form.name,
          resourceType: this.form.resourceType
        })
        
        if (res.data.code === 200) {
          const result = res.data.data
          
          // 填充生成的内容
          this.form.title = result.title || ''
          this.form.description = result.description || ''
          this.form.content = result.content || ''
          
          // 处理标签：将逗号分隔的字符串转为数组
          if (result.tags) {
            this.tagsList = result.tags.split(',').map(t => t.trim()).filter(t => t)
          }
          
          this.$message.success('AI生成成功！')
        } else {
          this.$message.error(res.data.message || 'AI生成失败')
        }
      } catch (error) {
        console.error('AI生成错误:', error)
        const errMsg = error.response?.data?.message || error.message || 'AI生成失败'
        if (errMsg.includes('402') || errMsg.includes('Payment Required')) {
          this.$message.error('DeepSeek API 余额不足，请先充值后使用')
        } else {
          this.$message.error('AI生成失败：' + errMsg)
        }
      } finally {
        this.aiGenerating = false
      }
    },
    handleSelectionChange(rows) {
      this.selectedRows = rows
    },
    clearSelection() {
      this.$refs.resourceTable.clearSelection()
      this.selectedRows = []
    },
    handleBatchTop(isTop) {
      if (!this.selectedRows || this.selectedRows.length === 0) {
        this.$message.warning('请先选择资源')
        return
      }
      const ids = this.selectedRows.map(r => r.id).filter(Boolean)
      this.$confirm(`确定${isTop ? '批量置顶' : '批量取消置顶'}选中资源？`, '提示', { type: 'warning' })
        .then(async () => {
          try {
            await this.$axios.post('/admin/resource/batch-top', { ids, isTop: isTop ? 1 : 0 })
            this.$message.success(`${isTop ? '批量置顶' : '批量取消置顶'}成功`)
            this.clearSelection()
            this.loadData()
          } catch (error) {
            console.error('批量置顶操作失败:', error)
            this.$message.error('操作失败')
          }
        })
        .catch(() => {})
    },
    // 批量推送到百度
    handleBatchPushBaidu() {
      if (!this.selectedRows || this.selectedRows.length === 0) {
        this.$message.warning('请先选择资源')
        return
      }
      // 从 localStorage 加载 token
      this.baiduToken = localStorage.getItem('baidu_push_token') || ''
      this.pushResult = null
      this.baiduPushDialogVisible = true
    },
    // 确认推送到百度
    async confirmPushBaidu() {
      this.pushingBaidu = true
      this.pushResult = null
      
      try {
        const ids = this.selectedRows.map(r => r.id).filter(Boolean)
        const requestData = { ids }
        
        // 只有当用户填写了token时才传递token，否则使用服务器默认token
        if (this.baiduToken && this.baiduToken.trim() !== '') {
          requestData.token = this.baiduToken
          // 保存 token 到 localStorage
          localStorage.setItem('baidu_push_token', this.baiduToken)
        }
        
        const res = await this.$axios.post('/seo/baidu/push', requestData)
        
        if (res.data.code === 200) {
          this.pushResult = res.data.data
          
          if (this.pushResult.success) {
            this.$message.success(`成功推送 ${this.pushResult.count} 个链接到百度`)
          } else {
            this.$message.warning(this.pushResult.message || '推送失败，请检查Token是否正确')
          }
        } else {
          this.$message.error(res.data.msg || '推送失败')
        }
      } catch (error) {
        console.error('推送到百度失败:', error)
        this.$message.error('推送失败')
      } finally {
        this.pushingBaidu = false
      }
    },
    // 打开百度站长平台帮助
    openBaiduHelp() {
      window.open('https://ziyuan.baidu.com/linksubmit/index', '_blank')
    },
    // 解析百度返回的配额信息
    getBaiduQuota(response) {
      try {
        if (!response) return ''
        const data = typeof response === 'string' ? JSON.parse(response) : response
        
        if (data.remain !== undefined) {
          return `今日剩余推送配额: ${data.remain} 条`
        }
        return ''
      } catch (e) {
        return ''
      }
    },
    // 删除封面
    removeCover() {
      this.$confirm('确定要删除封面图片吗？', '提示', {
        type: 'warning'
      }).then(() => {
        this.form.coverImage = ''
        this.coverDeleted = true // 标记用户主动删除了封面
        this.$message.success('已删除封面')
      }).catch(() => {})
    },
    // 触发上传
    triggerUpload() {
      if (this.$refs.coverUpload) {
        this.$refs.coverUpload.$children[0].$refs.input.click()
      }
    },
    // 显示裁剪对话框
    showCropDialog() {
      if (!this.form.coverImage) {
        this.$message.warning('请先上传封面图片')
        return
      }
      // 使用代理URL解决CORS问题
      if (this.form.coverImage.startsWith('https://sharefree.oss-cn-beijing.aliyuncs.com')) {
        this.cropImageSrc = `/api/upload/proxy-image?url=${encodeURIComponent(this.form.coverImage)}`
      } else {
        this.cropImageSrc = this.form.coverImage
      }
      this.cropDialogVisible = true
    },
    // 更新预览
    updatePreviews(data) {
      if (data) {
        this.previews = data
      }
    },
    // 关闭裁剪对话框
    handleCropDialogClose() {
      this.cropImageSrc = ''
      this.cropperReady = false
      this.previews = {}
    },
    // 裁剪对话框打开后
    handleCropDialogOpened() {
      // 延迟初始化裁剪器，确保DOM已渲染
      setTimeout(() => {
        this.cropperReady = true
        this.$nextTick(() => {
          if (this.$refs.cropper) {
            this.$refs.cropper.startCrop()
          }
        })
      }, 100)
    },
    // 确认裁剪
    async confirmCrop() {
      this.cropping = true
      try {
        // 获取裁剪后的blob
        this.$refs.cropper.getCropBlob(async (blob) => {
          try {
            // 上传裁剪后的图片
            const formData = new FormData()
            formData.append('file', blob, 'cover.jpg')
            
            const res = await this.$axios.post('/upload/cover', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
              }
            })
            
            if (res.data.code === 200) {
              this.form.coverImage = res.data.data.url
              this.$message.success('裁剪成功')
              this.cropDialogVisible = false
            } else {
              this.$message.error('上传失败')
            }
          } catch (error) {
            console.error('上传裁剪图片失败:', error)
            this.$message.error('上传失败')
          } finally {
            this.cropping = false
          }
        })
      } catch (error) {
        console.error('裁剪失败:', error)
        this.$message.error('裁剪失败')
        this.cropping = false
      }
    },
    // 处理粘贴事件
    async handlePaste(e) {
      // 只在对话框打开且没有封面时生效
      if (!this.dialogVisible || this.form.coverImage) {
        return
      }
      
      const items = (e.clipboardData || e.originalEvent.clipboardData).items
      for (let item of items) {
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault()
          const file = item.getAsFile()
          
          // 验证文件
          if (!this.beforeCoverUpload(file)) {
            return
          }
          
          // 上传
          const formData = new FormData()
          formData.append('file', file)
          
          try {
            const res = await this.$axios.post('/upload/cover', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
              }
            })
            
            if (res.data.code === 200) {
              this.form.coverImage = res.data.data.url
              this.$message.success('粘贴上传成功')
            } else {
              this.$message.error('上传失败')
            }
          } catch (error) {
            console.error('粘贴上传失败:', error)
            this.$message.error('上传失败')
          }
          break
        }
      }
    },
    async loadUserPermissions() {
      try {
        // 优先从菜单数据中读取权限列表（后端 /admin/permission/menu 返回的是对象数组：[{ key, name, ... }])
        const menuDataStr = localStorage.getItem('admin_menu_data')
        if (menuDataStr) {
          const menuData = JSON.parse(menuDataStr)
          const perms = menuData.permissions
          if (Array.isArray(perms)) {
            // 统一转换为字符串key数组
            this.userPermissions = perms
              .map(p => typeof p === 'string' ? p : (p.key || p.permissionKey))
              .filter(Boolean)
          }
        }
        
        // 兜底：从登录用户信息中读取（通常已是字符串数组）
        if (!this.userPermissions || this.userPermissions.length === 0) {
          const userStr = localStorage.getItem('admin_user')
          if (userStr) {
            const user = JSON.parse(userStr)
            this.userPermissions = (user.permissions || []).filter(Boolean)
          }
        }
      } catch (error) {
        console.error('加载用户权限失败:', error)
      }
    },
    async loadAvailableTags() {
      try {
        const res = await this.$axios.get('/admin/resource/tags')
        if (res.data.code === 200) {
          this.availableTags = res.data.data || []
        }
      } catch (e) {
        console.error('加载标签列表失败', e)
      }
    },
    hasPermission(permissionKey) {
      // 如果没有权限信息，默认显示（避免阻塞管理员操作）
      if (!this.userPermissions || this.userPermissions.length === 0) {
        return true
      }
      // 检查用户是否拥有指定权限
      return this.userPermissions.includes(permissionKey)
    },
    
    // 打开AI批量生成弹窗
    handleBatchAiGenerate() {
      this.batchAiDialogVisible = true
      this.batchAiForm = {
        nameList: '',
        resourceType: 0,
        categoryId: this.categories.length > 0 ? this.categories[0].id : null
      }
      this.batchAiProgress = 0
      this.batchAiStatus = null
      this.batchAiLogs = []
      this.batchAiProcessing = false
    },
    
    // 开始批量AI生成
    async startBatchAiGenerate() {
      // 解析资源名称列表
      const nameList = this.batchAiForm.nameList
        .split('\n')
        .map(name => name.trim())
        .filter(name => name.length > 0)
      
      if (nameList.length === 0) {
        this.$message.warning('请至少输入一个资源名称')
        return
      }
      
      // 生成任务ID
      const taskId = 'task_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
      
      // 添加到任务中心（移到确认框外面，确保点击开始生成就添加任务）
      const layout = this.$parent.$parent
      if (layout && typeof layout.addTask === 'function') {
        layout.addTask({
          id: taskId,
          title: 'AI批量生成资源',
          description: `正在生成 ${nameList.length} 个资源`,
          status: 'running',
          progress: 0,
          createTime: new Date().toISOString()
        })
      }
      
      this.$confirm(`将生成 ${nameList.length} 个资源，确认开始？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        this.batchAiProcessing = true
        this.batchAiProgress = 0
        this.batchAiStatus = null
        this.batchAiLogs = []
        
        this.addBatchAiLog(`开始批量生成，共 ${nameList.length} 个资源`, 'info')
        
        let successCount = 0
        let failCount = 0
        const failedList = [] // 记录失败的资源
        
        for (let i = 0; i < nameList.length; i++) {
          const name = nameList[i]
          const progress = Math.round(((i + 1) / nameList.length) * 100)
          this.batchAiProgress = progress
          
          // 更新任务中心进度
          if (layout && typeof layout.updateTaskProgress === 'function') {
            layout.updateTaskProgress(taskId, progress, `已处理 ${i + 1}/${nameList.length}`)
          }
          
          this.addBatchAiLog(`[${i + 1}/${nameList.length}] 正在生成: ${name}`, 'info')
          
          try {
            // 调用AI生成接口
            const aiRes = await this.$axios.post('/admin/ai/generate-resource-info', {
              name: name,
              resourceType: this.batchAiForm.resourceType
            })
            
            if (aiRes.data.code === 200) {
              const aiData = aiRes.data.data
              
              // 保存资源
              const resource = {
                name: name,
                title: aiData.title || name,
                description: aiData.description || '',
                content: aiData.content || '',
                tags: aiData.tags || '',
                categoryId: this.batchAiForm.categoryId,
                resourceType: this.batchAiForm.resourceType,
                isFree: 1,
                status: 0, // 草稿状态
                coverImage: '',
                shareType: '',
                shareLink: ''
              }
              
              const saveRes = await this.$axios.post('/admin/resource', resource)
              
              if (saveRes.data.code === 200) {
                successCount++
                this.addBatchAiLog(`  ✅ 生成成功: ${name}`, 'success')
              } else {
                failCount++
                const errorMsg = saveRes.data.message || '未知错误'
                failedList.push({ name, reason: `保存失败: ${errorMsg}` })
                this.addBatchAiLog(`  ❌ 保存失败: ${name} - ${errorMsg}`, 'error')
              }
            } else {
              failCount++
              const errorMsg = aiRes.data.message || '未知错误'
              failedList.push({ name, reason: `AI生成失败: ${errorMsg}` })
              this.addBatchAiLog(`  ❌ AI生成失败: ${name} - ${errorMsg}`, 'error')
            }
          } catch (error) {
            failCount++
            const errorMsg = error.message || '网络错误'
            failedList.push({ name, reason: errorMsg })
            this.addBatchAiLog(`  ❌ 处理失败: ${name} - ${errorMsg}`, 'error')
          }
        }
        
        this.batchAiProgress = 100
        this.batchAiStatus = failCount === 0 ? 'success' : (successCount > 0 ? 'warning' : 'exception')
        
        this.addBatchAiLog('', 'info')
        this.addBatchAiLog('='.repeat(50), 'info')
        this.addBatchAiLog(`批量生成完成！`, 'success')
        this.addBatchAiLog(`总计: ${nameList.length} 个  |  成功: ${successCount} 个  |  失败: ${failCount} 个`, 
          failCount === 0 ? 'success' : 'warning')
        this.addBatchAiLog('='.repeat(50), 'info')
        
        // 如果有失败的，显示失败明细
        if (failedList.length > 0) {
          this.addBatchAiLog('', 'info')
          this.addBatchAiLog('失败资源明细：', 'error')
          failedList.forEach((item, index) => {
            this.addBatchAiLog(`${index + 1}. ${item.name} - ${item.reason}`, 'error')
          })
        }
        
        // 更新任务中心状态
        if (layout && typeof layout.completeTask === 'function') {
          const result = `成功: ${successCount} 个，失败: ${failCount} 个`
          if (failCount === 0) {
            layout.completeTask(taskId, result)
          } else {
            layout.failTask(taskId, `部分失败: ${result}`)
          }
        }
        
        this.batchAiProcessing = false
        
        // 刷新列表
        this.loadData()
        
        // 3秒后自动关闭弹窗（如果用户没有手动关闭）
        setTimeout(() => {
          if (this.batchAiDialogVisible && failCount === 0) {
            this.batchAiDialogVisible = false
          }
        }, 3000)
      }).catch(() => {
        // 取消任务
        if (layout && typeof layout.failTask === 'function') {
          layout.failTask(taskId, '用户取消')
        }
        // 如果任务列表中没有这个任务，也要移除它
        if (layout && layout.tasks) {
          const taskIndex = layout.tasks.findIndex(t => t.id === taskId)
          if (taskIndex > -1) {
            layout.tasks.splice(taskIndex, 1)
          }
        }
      })
    },
    
    // 添加批量AI日志
    addBatchAiLog(message, type = 'info') {
      this.batchAiLogs.push({ message, type })
      // 自动滚动到底部
      this.$nextTick(() => {
        const logContainer = document.querySelector('.batch-ai-logs')
        if (logContainer) {
          logContainer.scrollTop = logContainer.scrollHeight
        }
      })
    },
    
    // 格式化批量进度
    formatBatchProgress(percentage) {
      return `${percentage}%`
    },
    
    // 初始化富文本编辑器粘贴处理
    initEditorPasteHandler() {
      // 使用多种方式尝试获取quill编辑器实例
      let quillEditor = null
      
      // 方法1：通过$children查找
      quillEditor = this.$children.find(child => child.$options.name === 'quillEditor')
      
      // 方法2：通过$refs查找（如果有ref属性）
      if (!quillEditor && this.$refs.quillEditor) {
        quillEditor = this.$refs.quillEditor
      }
      
      // 方法3：直接查找所有子组件中的quill实例
      if (!quillEditor) {
        const findQuill = (components) => {
          for (let component of components) {
            if (component.quill) {
              return component
            }
            if (component.$children && component.$children.length > 0) {
              const found = findQuill(component.$children)
              if (found) return found
            }
          }
          return null
        }
        quillEditor = findQuill(this.$children)
      }
      
      if (!quillEditor) {
        console.warn('未找到富文本编辑器实例')
        return
      }
      
      this.quillEditor = quillEditor
      const editor = quillEditor.quill
      
      if (!editor) {
        console.warn('未找到quill实例')
        return
      }
      
      console.log('编辑器初始化成功:', editor)
      
      // 监听粘贴事件
      editor.root.addEventListener('paste', this.handleEditorPaste, false)
    },
    
    // 处理图片上传（点击上传按钮）
    handleImageUpload() {
      const input = document.createElement('input')
      input.setAttribute('type', 'file')
      input.setAttribute('accept', 'image/*')
      input.click()

      input.onchange = async () => {
        const file = input.files[0]
        if (!file) return

        try {
          // 显示上传提示
          const loadingMsg = this.$message({
            message: '图片上传中...',
            duration: 0,
            iconClass: 'el-icon-loading'
          })

          const formData = new FormData()
          // GIF 动图直接原图上传，避免压缩丢失动画
          if (file.type === 'image/gif') {
            formData.append('file', file)
          } else {
            // 非 GIF 进行压缩后上传
            const compressedFile = await this.compressEditorImage(file)
            formData.append('file', compressedFile)
          }

          const token = localStorage.getItem('admin_token')
          const response = await fetch('/api/upload/editor-image', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          })

          const result = await response.json()
          loadingMsg.close()
          
          console.log('上传响应:', result) // 调试日志

          if (result.code === 200) {
            console.log('图片URL:', result.data.url) // 调试日志
            
            // 确保编辑器已初始化
            if (!this.quillEditor) {
              console.log('第一次初始化编辑器')
              this.initEditorPasteHandler()
            }
            
            // 获取quill实例
            let quill = this.quillEditor ? this.quillEditor.quill : null
            
            // 如果还是null，尝试直接查找
            if (!quill) {
              console.log('直接查找quill实例')
              const findQuill = (components) => {
                for (let component of components) {
                  if (component.quill) {
                    return component.quill
                  }
                  if (component.$children && component.$children.length > 0) {
                    const found = findQuill(component.$children)
                    if (found) return found
                  }
                }
                return null
              }
              quill = findQuill(this.$children)
            }
            
            if (!quill) {
              console.error('编辑器未就绪')
              this.$message.error('编辑器未就绪，请在编辑器中点击后重试')
              return
            }
            
            console.log('quill实例:', quill)
            
            const range = quill.getSelection() || { index: quill.getLength() }
            console.log('插入位置:', range.index) // 调试日志
            
            // 插入图片
            quill.insertEmbed(range.index, 'image', result.data.url)
            // 移动光标到图片后面
            quill.setSelection(range.index + 1)
            
            console.log('图片已插入')
            this.$message.success('图片上传成功')
          } else {
            this.$message.error('图片上传失败: ' + (result.message || '未知错误'))
          }
        } catch (error) {
          console.error('图片上传失败:', error)
          this.$message.error('图片上传失败')
        }
      }
    },
    
    // 处理富文本编辑器粘贴事件
    async handleEditorPaste(e) {
      const clipboardData = e.clipboardData || window.clipboardData
      if (!clipboardData) return
      
      const items = clipboardData.items
      if (!items) return
      
      // 检查是否有图片
      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        if (item.type.indexOf('image') !== -1) {
          // 阻止默认粘贴行为
          e.preventDefault()
          
          const file = item.getAsFile()
          if (!file) continue
          
          try {
            // 显示上传提示
            const loadingMsg = this.$message({
              message: '图片上传中...',
              duration: 0,
              iconClass: 'el-icon-loading'
            })
            
            const formData = new FormData()
            // GIF 动图直接原图上传，避免压缩丢失动画
            if (file.type === 'image/gif') {
              formData.append('file', file)
            } else {
              // 非 GIF 进行压缩后上传
              const compressedFile = await this.compressEditorImage(file)
              formData.append('file', compressedFile)
            }
            
            const token = localStorage.getItem('admin_token')
            const response = await fetch('/api/upload/editor-image', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`
              },
              body: formData
            })
            
            const result = await response.json()
            loadingMsg.close()
            
            if (result.code === 200) {
              // 确保编辑器已初始化
              if (!this.quillEditor) {
                this.initEditorPasteHandler()
              }
              
              // 获取当前光标位置
              const quill = this.quillEditor ? this.quillEditor.quill : null
              if (!quill) {
                this.$message.error('编辑器未就绪，请重试')
                return
              }
              
              const range = quill.getSelection() || { index: quill.getLength() }
              // 插入图片
              quill.insertEmbed(range.index, 'image', result.data.url)
              // 移动光标到图片后面
              quill.setSelection(range.index + 1)
              this.$message.success('图片上传成功')
            } else {
              this.$message.error('图片上传失败: ' + (result.message || '未知错误'))
            }
          } catch (error) {
            console.error('粘贴图片上传失败:', error)
            this.$message.error('图片上传失败')
          }
          
          break
        }
      }
    },
    
    // 压缩编辑器图片
    compressEditorImage(file) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const img = new Image()
          img.onload = () => {
            const canvas = document.createElement('canvas')
            let width = img.width
            let height = img.height
            
            // 最大宽度1200px
            const maxWidth = 1200
            if (width > maxWidth) {
              height = (height * maxWidth) / width
              width = maxWidth
            }
            
            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0, width, height)
            
            // 压缩质量0.7
            canvas.toBlob((blob) => {
              const compressedFile = new File([blob], file.name || 'pasted-image.jpg', {
                type: 'image/jpeg',
                lastModified: Date.now()
              })
              resolve(compressedFile)
            }, 'image/jpeg', 0.7)
          }
          img.src = e.target.result
        }
        reader.readAsDataURL(file)
      })
    },
    
    // 封面图片粘贴处理
    async handlePaste(e) {
      // 只在没有封面时生效
      if (this.form.coverImage) {
        return
      }
      
      const items = (e.clipboardData || e.originalEvent.clipboardData).items
      for (let item of items) {
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault()
          const file = item.getAsFile()
          
          // 验证文件
          if (!this.beforeCoverUpload(file)) {
            return
          }
          
          // 上传
          const formData = new FormData()
          formData.append('file', file)
          
          try {
            const res = await this.$axios.post('/upload/cover', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
              }
            })
            
            if (res.data.code === 200) {
              this.form.coverImage = res.data.data.url
              this.$message.success('粘贴上传成功')
            } else {
              this.$message.error('上传失败')
            }
          } catch (error) {
            console.error('粘贴上传失败:', error)
            this.$message.error('上传失败')
          }
          break
        }
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 20px;
  background: transparent;
  min-height: calc(100vh - 60px);
}

/* 封面上传区域 */
.cover-upload-container {
  display: inline-block;
}

.cover-uploader {
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 300px;
  height: 225px;
  display: inline-block;
  background-color: #fafafa;
  transition: all 0.3s;
}

.cover-uploader ::v-deep .el-upload {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cover-uploader ::v-deep .el-upload-dragger {
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cover-uploader:hover {
  border-color: #409EFF;
  background-color: #f0f7ff;
}

.cover-preview-wrapper {
  width: 300px;
  height: 225px;
  position: relative;
  border: 2px solid #e4e7ed;
  border-radius: 6px;
  overflow: hidden;
  background: #000;
}

.cover-preview-wrapper:hover .cover-actions {
  opacity: 1;
}

.cover-actions {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  padding: 12px;
  display: flex;
  gap: 8px;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.cover-uploader-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8c939d;
  padding: 20px;
  text-align: center;
}

.cover-uploader-icon {
  font-size: 48px;
  color: #8c939d;
  margin-bottom: 16px;
  transition: all 0.3s;
}

.cover-uploader:hover .cover-uploader-icon {
  color: #409EFF;
  transform: scale(1.1);
}

.cover-uploader-text {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  margin-bottom: 8px;
}

.cover-uploader-text em {
  color: #409EFF;
  font-style: normal;
}

.cover-uploader-tip {
  font-size: 12px;
  color: #909399;
}

.cover-uploader:hover .cover-uploader-text {
  color: #409EFF;
}

.cover-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  background: #000;
}

.tip {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}

/* 富文本编辑器样式 */
.quill-editor ::v-deep .ql-container {
  height: 350px;
  overflow-y: auto;
}

.quill-editor ::v-deep .ql-editor {
  min-height: 300px;
  font-size: 14px;
  line-height: 1.6;
}

/* 操作按钮自适应 */
.action-buttons {
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  align-items: center;
  justify-content: flex-start;
}

.action-buttons .el-button {
  margin: 0;
  padding: 7px 12px;
  font-size: 12px;
  border-radius: 4px;
  transition: all 0.3s;
}

.action-buttons .el-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.action-buttons .el-button--mini {
  min-width: 60px;
}
.desc-cell {
  max-width: 300px;
  height: 44px;
  line-height: 22px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* 裁剪对话框样式 */
.crop-container {
  display: flex;
  gap: 30px;
  min-height: 500px;
}

.crop-main {
  flex: 1;
  min-width: 0;
}

.crop-area {
  width: 100%;
  height: 500px;
  background: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
}

.crop-preview {
  width: 250px;
  flex-shrink: 0;
}

.preview-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
  text-align: center;
}

.preview-box {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
  margin: 0 auto;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.preview-container {
  overflow: hidden;
}

.preview-container img {
  display: block;
  max-width: none;
}
</style>
